import { useCallback, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOkrPageState, OKR_ITEMS_PER_PAGE } from "@/hooks/useOkrPageState";
import { useOkrReorder } from "@/hooks/useOkrReorder";
import { useOkrCycles } from "@/hooks/useOkrCycles";
import { useOkrOverview } from "@/hooks/useOkrOverview";
import { useOkrDialogs } from "@/components/okr/use-okr-dialogs";
import { PageShell } from "@/components/layout/PageShell";
import { OkrPageHeader, type OkrView } from "@/components/okr/OkrPageHeader";
import { OkrPacingCard } from "@/components/okr/OkrPacingCard";
import { OkrCategoryCards } from "@/components/okr/OkrCategoryCards";
import { OkrCoverageView } from "@/components/okr/OkrCoverageView";
import { OkrFilters } from "@/components/okr/OkrFilters";
import { ObjectiveList } from "@/components/okr/ObjectiveList";
import { ObjectiveDetailSheet } from "@/components/okr/ObjectiveDetailSheet";
import { ObjectiveDialogs } from "@/components/okr/ObjectiveDialogs";
import { KeyResultDialogs } from "@/components/okr/KeyResultDialogs";
import { ModernPagination } from "@/components/admin/ModernPagination";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { OBJECTIVE_CATEGORIES } from "@/components/okr/okrConstants";
import {
  resolveCycle,
  computeCyclePacing,
  computeCategoryRollups,
  buildCoverageRows,
  pppsWithoutKr,
  pppsWithNoDeliverable,
} from "@/components/okr/okr-overview";
import { isOkrLeaderOnly } from "@/lib/okr-errors";

const OKRs = () => {
  const { canEditOkr, canViewOkr } = useAuth();
  const state = useOkrPageState();
  const dialogs = useOkrDialogs();
  const [expandedPids, setExpandedPids] = useState<Set<string>>(new Set());
  const [view, setView] = useState<OkrView>("objectives");

  const objectives = useMemo(() => state.query.data?.data ?? [], [state.query.data?.data]);
  // Reorder sends page-local positions, so it is only correct while the whole list is on this page.
  const total = state.query.data?.total ?? 0;
  const totalPages = Math.ceil(total / OKR_ITEMS_PER_PAGE);

  const { onDragEnd, isPending: reorderPending } = useOkrReorder({ objectives, params: state.params, page: state.page });

  // Dashboard data: company-wide, scoped to the resolved cycle (selected filter, else the active cycle).
  const cyclesQuery = useOkrCycles();
  const cycles = useMemo(() => cyclesQuery.data ?? [], [cyclesQuery.data]);
  const resolvedCycle = useMemo(() => resolveCycle(cycles, state.cyclePid), [cycles, state.cyclePid]);
  const overview = useOkrOverview(state.cyclePid || resolvedCycle?.pid, {
    cyclesReady: !cyclesQuery.isLoading && canViewOkr,
    coverage: view === "coverage" && canViewOkr,
  });

  const pacing = useMemo(
    () => (resolvedCycle ? computeCyclePacing(resolvedCycle, overview.objectives) : null),
    [resolvedCycle, overview.objectives],
  );
  const categoryRollups = useMemo(
    () => computeCategoryRollups(overview.objectives, OBJECTIVE_CATEGORIES),
    [overview.objectives],
  );
  // Coverage ignores the status filter but respects category, per spec.
  const coverageObjectives = useMemo(
    () => (state.category ? overview.objectives.filter((o) => o.category === state.category) : overview.objectives),
    [overview.objectives, state.category],
  );
  const coverage = useMemo(
    () => buildCoverageRows(coverageObjectives, overview.keyResults, overview.ppps),
    [coverageObjectives, overview.keyResults, overview.ppps],
  );

  const handleToggleExpand = useCallback((pid: string) => {
    setExpandedPids((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  }, []);

  if (!canViewOkr || isOkrLeaderOnly(state.query.error)) {
    return (
      <PageShell>
        <div className="rounded-xl border border-dashed border-border bg-muted/40 p-12 text-center">
          <h2 className="text-lg font-semibold">OKRs are visible to leaders only</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Ask a director if you need access to the company OKR tree.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <OkrPageHeader
        totalObjectives={total}
        view={view}
        onViewChange={setView}
        right={
          canEditOkr ? (
            <Button
              variant="gradient"
              className="shrink-0 gap-2 shadow-sm rounded-full px-6"
              onClick={dialogs.openCreateObjective}
            >
              <Plus className="h-4 w-4" /> Add Objective
            </Button>
          ) : undefined
        }
      />

      {overview.isError ? (
        <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          Could not load the OKR overview. The list below may still work.
        </div>
      ) : (
        <>
          {resolvedCycle && pacing && <OkrPacingCard cycle={resolvedCycle} pacing={pacing} />}
          <OkrCategoryCards rollups={categoryRollups} activeCategory={state.category} onSelectCategory={state.setCategory} />
        </>
      )}

      <OkrFilters
        search={state.search}
        onSearchChange={state.setSearch}
        statusFilter={state.statusFilter}
        onStatusFilterChange={state.setStatusFilter}
        viewMode={state.viewMode}
        onViewModeChange={state.setViewMode}
        cyclePid={state.cyclePid}
        onCycleChange={state.setCyclePid}
        category={state.category}
        onCategoryChange={state.setCategory}
      />

      {view === "objectives" ? (
        <>
          <ObjectiveList
            objectives={objectives}
            isLoading={state.query.isLoading}
            isError={state.query.isError}
            canManage={canEditOkr}
            isDragEnabled={canEditOkr && !state.hasFilters && !reorderPending && total <= OKR_ITEMS_PER_PAGE}
            isKrDragEnabled={canEditOkr && !reorderPending}
            onDragEnd={onDragEnd}
            expandedPids={expandedPids}
            onToggleExpand={handleToggleExpand}
            hasFilters={state.hasFilters}
            {...dialogs.list}
          />

          <ModernPagination
            page={state.page}
            totalPages={totalPages}
            onPageChange={state.setPage}
            total={total}
            limit={OKR_ITEMS_PER_PAGE}
          />
        </>
      ) : overview.isError ? null : (
        <OkrCoverageView
          rows={coverage.rows}
          unlinkedKrs={coverage.unlinkedKrs}
          pppsWithoutKr={pppsWithoutKr(overview.ppps)}
          pppsNoDeliverable={pppsWithNoDeliverable(overview.ppps)}
          isLoading={overview.isLoading}
          truncated={overview.truncated}
        />
      )}

      <ObjectiveDetailSheet {...dialogs.sheet} canManage={canEditOkr} />
      <ObjectiveDialogs {...dialogs.objectiveDialogs} />
      <KeyResultDialogs {...dialogs.keyResultDialogs} />
    </PageShell>
  );
};

export default OKRs;
